// combatpro/encounter/encounterBriefEngine.js
//
// Combat PRO - Encounter Brief Engine (pure, UI-independent).
//
// The Encounter Brief is a battle-specific layer describing what makes THIS
// encounter different while it is being run: goal / victory / failure, tactical
// battle setup, structured events (waves, reinforcements, phase changes) and
// special rules (hazards, custom rules, monster variants). It is NOT a mission,
// campaign or story system.
//
// This module owns the brief data SHAPE and the pure logic over it: default
// shape, id minting, immutable event/rule editing, trigger evaluation against
// live combat state (round / HP), run-state summaries and an "is configured"
// test. It owns NO React state and performs NO side effects (no storage, no
// window, no DOM, no fetch). CombatContext owns the state and calls these.
//
// The brief is stored per encounter on encounterStore.encounters[i].encounterBrief
// so it persists, switches and duplicates with the encounter through the existing
// store medium. No new backend endpoint.
//
// Style: no optional chaining, no nullish coalescing, no em dash.

import { normalizeZones } from '../runtime/dmOpsEngine';

// Vocabularies (kept small and battle-specific).
export var EVENT_KINDS = ['wave', 'reinforcement', 'phase', 'timed', 'manual'];
export var EVENT_KIND_LABELS = {
  wave: 'Wave', reinforcement: 'Reinforcement', phase: 'Phase Change',
  timed: 'Timed Event', manual: 'Manual Event'
};
export var TRIGGER_TYPES = ['round', 'hp', 'group-defeated', 'manual'];
export var TRIGGER_LABELS = {
  round: 'Round', hp: 'HP threshold', 'group-defeated': 'Group defeated', manual: 'Manual'
};
export var EVENT_STATUSES = ['pending', 'active', 'resolved'];
export var RULE_KINDS = ['hazard', 'custom', 'variant'];
export var RULE_KIND_LABELS = { hazard: 'Hazard / Environment', custom: 'Custom Rule', variant: 'Monster Variant' };
export var RESOLUTIONS = ['open', 'achieved', 'failed', 'resolved'];
export var RESOLUTION_LABELS = { open: 'Open', achieved: 'Achieved', failed: 'Failed', resolved: 'Resolved' };

// A stable-ish unique id. Combines time and a random suffix; ids only need to be
// unique within one brief's small arrays, so this is sufficient and dependency free.
export function newBriefId(prefix) {
  var p = prefix ? prefix : 'eb';
  return p + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
}

// The empty brief shape. Every field defaults so an encounter with no brief simply
// has an empty/default brief and behaves exactly as it does now.
export function defaultBrief() {
  return {
    goal: { objective: '', victory: '', failure: '', endCondition: '', resolution: 'open' },
    setup: { trigger: '', entry: '', lines: [], zones: [] },
    events: [],
    rules: []
  };
}

// Coerce any stored/legacy value into a valid brief shape (used by normalizeStore
// so loaded stores and partial data upgrade cleanly). Never throws.
export function normalizeBrief(brief) {
  var d = defaultBrief();
  if (!brief || typeof brief !== 'object') { return d; }
  var g = (brief.goal && typeof brief.goal === 'object') ? brief.goal : {};
  d.goal.objective = strOr(g.objective, '');
  d.goal.victory = strOr(g.victory, '');
  d.goal.failure = strOr(g.failure, '');
  d.goal.endCondition = strOr(g.endCondition, '');
  d.goal.resolution = (RESOLUTIONS.indexOf(g.resolution) !== -1) ? g.resolution : 'open';
  var s = (brief.setup && typeof brief.setup === 'object') ? brief.setup : {};
  d.setup.trigger = strOr(s.trigger, '');
  d.setup.entry = strOr(s.entry, '');
  d.setup.lines = Array.isArray(s.lines) ? s.lines.filter(isObj).map(function (l) {
    return { id: strOr(l.id, newBriefId('set')), text: strOr(l.text, '') };
  }) : [];
  d.setup.zones = normalizeZones(s.zones);
  d.events = Array.isArray(brief.events) ? brief.events.filter(isObj).map(normalizeEvent) : [];
  d.rules = Array.isArray(brief.rules) ? brief.rules.filter(isObj).map(normalizeRule) : [];
  return d;
}

function normalizeEvent(e) {
  var t = (e.trigger && typeof e.trigger === 'object') ? e.trigger : {};
  return {
    id: strOr(e.id, newBriefId('evt')),
    name: strOr(e.name, ''),
    kind: (EVENT_KINDS.indexOf(e.kind) !== -1) ? e.kind : 'wave',
    monsters: strOr(e.monsters, ''),
    trigger: {
      type: (TRIGGER_TYPES.indexOf(t.type) !== -1) ? t.type : 'manual',
      round: numOrNull(t.round),
      hpPct: numOrNull(t.hpPct),
      slot: numOrNull(t.slot),
      note: strOr(t.note, '')
    },
    entry: strOr(e.entry, ''),
    status: (EVENT_STATUSES.indexOf(e.status) !== -1) ? e.status : 'pending'
  };
}

function normalizeRule(r) {
  return {
    id: strOr(r.id, newBriefId('rule')),
    kind: (RULE_KINDS.indexOf(r.kind) !== -1) ? r.kind : 'custom',
    title: strOr(r.title, ''),
    detail: strOr(r.detail, ''),
    slot: numOrNull(r.slot),
    active: r.active === true
  };
}

function isObj(x) { return x && typeof x === 'object'; }
function strOr(x, f) { return (typeof x === 'string') ? x : f; }
function numOrNull(x) {
  if (x === null || typeof x === 'undefined' || x === '') { return null; }
  var n = Number(x);
  return isNaN(n) ? null : n;
}

// Factories for a new event / rule (used by the add handlers).
export function newEvent(kind) {
  var k = (EVENT_KINDS.indexOf(kind) !== -1) ? kind : 'wave';
  var triggerType = (k === 'phase') ? 'hp' : (k === 'manual' ? 'manual' : 'round');
  return {
    id: newBriefId('evt'), name: '', kind: k, monsters: '',
    trigger: { type: triggerType, round: (triggerType === 'round' ? 2 : null), hpPct: (triggerType === 'hp' ? 50 : null), slot: null, note: '' },
    entry: '', status: 'pending'
  };
}

export function newRule(kind) {
  var k = (RULE_KINDS.indexOf(kind) !== -1) ? kind : 'custom';
  return { id: newBriefId('rule'), kind: k, title: '', detail: '', slot: null, active: false };
}

// Is the brief configured with anything worth showing / persisting beyond default?
export function briefIsConfigured(brief) {
  if (!brief) { return false; }
  var g = brief.goal || {};
  if (trim(g.objective) || trim(g.victory) || trim(g.failure) || trim(g.endCondition) || (g.resolution && g.resolution !== 'open')) { return true; }
  var s = brief.setup || {};
  if (trim(s.trigger) || trim(s.entry) || (Array.isArray(s.lines) && s.lines.length)) { return true; }
  if (Array.isArray(brief.events) && brief.events.length) { return true; }
  if (Array.isArray(brief.rules) && brief.rules.length) { return true; }
  return false;
}

function trim(x) { return (typeof x === 'string') ? x.trim() : ''; }

// Evaluate whether an event's trigger condition is currently MET by live combat
// state. This is a HINT for the DM (surfaced in the UI); it never auto-activates.
// ctx = { round: Number, monsters: Array }.
//   round trigger -> current round >= trigger.round
//   hp trigger    -> referenced monster slot currentHP <= hpPct% of maxHP
//   group-defeated / manual -> always false (DM discretion)
export function evaluateEventDue(event, ctx) {
  if (!event || !event.trigger) { return false; }
  if (event.status === 'resolved') { return false; }
  var t = event.trigger;
  var round = (ctx && typeof ctx.round === 'number') ? ctx.round : 1;
  var monsters = (ctx && Array.isArray(ctx.monsters)) ? ctx.monsters : [];
  if (t.type === 'round') {
    return (typeof t.round === 'number') && round >= t.round;
  }
  if (t.type === 'hp') {
    if (typeof t.slot !== 'number' || typeof t.hpPct !== 'number') { return false; }
    var m = monsters[t.slot];
    if (!m) { return false; }
    var max = Number(m.maxHP);
    var cur = Number(m.currentHP);
    if (isNaN(max) || max <= 0) { return false; }
    if (isNaN(cur)) { cur = max; }
    return (cur / max) * 100 <= t.hpPct;
  }
  return false;
}

// Short human label for an event's trigger, for card summaries.
export function triggerLabel(event) {
  if (!event || !event.trigger) { return 'Manual'; }
  var t = event.trigger;
  if (t.type === 'round') { return 'Round ' + (typeof t.round === 'number' ? t.round : '?'); }
  if (t.type === 'hp') { return (typeof t.hpPct === 'number' ? (t.hpPct + '% HP') : 'HP threshold'); }
  if (t.type === 'group-defeated') { return 'Group defeated'; }
  return 'Manual';
}

// The single most useful "next event" for the run-state summary: the first
// non-resolved event, preferring pending round-events by ascending round, then any
// other non-resolved event. Returns the event object or null.
export function nextEvent(brief, round) {
  if (!brief || !Array.isArray(brief.events)) { return null; }
  var live = brief.events.filter(function (e) { return e && e.status !== 'resolved'; });
  if (!live.length) { return null; }
  var roundEvents = live.filter(function (e) { return e.trigger && e.trigger.type === 'round' && typeof e.trigger.round === 'number'; })
    .sort(function (a, b) { return a.trigger.round - b.trigger.round; });
  var upcoming = roundEvents.filter(function (e) { return e.trigger.round >= (typeof round === 'number' ? round : 1); });
  if (upcoming.length) { return upcoming[0]; }
  if (roundEvents.length) { return roundEvents[roundEvents.length - 1]; }
  return live[0];
}

// Counts used by the overview cards. Only ever reports what exists.
export function briefSummary(brief) {
  var b = brief ? brief : defaultBrief();
  var events = Array.isArray(b.events) ? b.events : [];
  var rules = Array.isArray(b.rules) ? b.rules : [];
  var setup = b.setup || {};
  var setupCount = (trim(setup.trigger) ? 1 : 0) + (trim(setup.entry) ? 1 : 0) + (Array.isArray(setup.lines) ? setup.lines.length : 0);
  return {
    goalSet: !!(trim((b.goal || {}).objective) || trim((b.goal || {}).victory) || trim((b.goal || {}).failure) || trim((b.goal || {}).endCondition)),
    resolution: (b.goal && b.goal.resolution) ? b.goal.resolution : 'open',
    setupCount: setupCount,
    eventCount: events.length,
    eventsResolved: events.filter(function (e) { return e.status === 'resolved'; }).length,
    eventsActive: events.filter(function (e) { return e.status === 'active'; }).length,
    ruleCount: rules.length,
    hazardsActive: rules.filter(function (r) { return r.kind === 'hazard' && r.active; }).length
  };
}
